import { useState, type ReactNode } from "react";
import { List, SignOut, X } from "@phosphor-icons/react";
import { Button, LanguageSwitch, Wordmark, cn, useListKeys } from "@ghezaa/ui";
import { useShellSession } from "./shell-session";
import { AppLauncher } from "./app-launcher";
import { ViewAsBanner } from "./view-as-banner";
import { Trans, useLingui } from "@lingui/react/macro";

/** Class names for nav links. Apps render their own typed router `Link`s with these so the shell stays router-agnostic. */
export const navLinkClass =
  "flex h-8 items-center gap-2.5 px-2 text-sm text-ink-2 transition-colors duration-150 ease-out-quick hover:text-ink";
export const navLinkActiveClass = "bg-surface font-medium text-ink";
export const navFooterLinkClass =
  "flex h-8 items-center gap-2.5 px-2 text-xs text-ink-3 transition-colors duration-150 ease-out-quick hover:text-ink";
/** Class for the app's router `Link` around the logo (`home`). */
export const homeLinkClass = "flex px-2";

export type AppShellProps = {
  /** "Core", "Orders", "Support" — sits beside the logo as an eyebrow. */
  appName: string;
  /**
   * Makes the logo the way back to the app's root: wrap the wordmark in the app's own router `Link` with
   * `homeLinkClass`, e.g. `(mark) => <Link to="/" className={homeLinkClass} aria-label="Orders home">{mark}</Link>`.
   */
  home?: (wordmark: ReactNode) => ReactNode;
  /**
   * What every page of the app is looking at (Orders' market): under the logo, above the navigation, so it reads as the
   * frame of every page rather than a control of one. Never put such a control in a page header.
   */
  scope?: ReactNode;
  /** The primary navigation: the app's own `Link`s using `navLinkClass` / `navLinkActiveClass`. */
  nav: ReactNode;
  /** Optional links above the user block (design specimen, help, back to Core). */
  footer?: ReactNode;
  user: { name: string | null; email: string | null };
  /** Where sign-out lands: the app's own root, "/" unless the app lives under a prefix (Orders' back office is "/staff"). */
  signOutTo?: string;
  /** Core's View as a role page, for "Switch role" on the view-as banner (C4): `${coreUrl(...)}/view-as`. */
  switchRoleHref?: string;
  children: ReactNode;
};

/**
 * 240px canvas sidebar with a right hairline; content on the surface; the switcher (C7) beside the logo. Below `md` the
 * sidebar becomes a slim bar — the logo, the switcher and a Menu button — and the navigation opens in place under it
 * (never over the page), closing again when a link is followed. Pages take the width beside the sidebar, up to 1680px
 * from its edge: tables are the point of most pages, and forms and prose keep their own narrower measure.
 */
export function AppShell({ appName, home, scope, nav, footer, user, signOutTo, switchRoleHref, children }: AppShellProps) {
  // The person's language, chosen in any app; the switch saves a new one (and shows only where the app offers two).
  const { signOut, chooseLanguage, reloading } = useShellSession(signOutTo);
  const [open, setOpen] = useState(false);
  // `/` to search, j and k through a list's rows, on every page of every back office.
  useListKeys();
  const { t } = useLingui();
  if (reloading) return <div className="min-h-dvh bg-canvas" aria-busy="true" />;

  return (
    <div className="grid min-h-dvh grid-cols-1 grid-rows-[auto_1fr] md:grid-cols-[240px_1fr] md:grid-rows-1 print:block">
      {/* Pinned at the window's height; on a short window its own content scrolls, so it never runs past the page. */}
      <aside className="flex flex-col border-b border-line bg-canvas px-4 py-3 md:sticky md:top-0 md:h-dvh md:overflow-y-auto md:border-b-0 md:border-e md:py-5 print:hidden">
        {/* A long app name ("Delivery Notes") drops under the logo rather than push the switcher out of the sidebar. */}
        <div className="flex items-center justify-between gap-2">
          {home ? (
            // On a phone the logo also closes an open menu, like any link inside it.
            <div className="min-w-0" onClick={() => setOpen(false)}>{home(<Wordmark app={appName} className="flex-wrap gap-y-1" />)}</div>
          ) : (
            <Wordmark app={appName} className="min-w-0 flex-wrap gap-y-1 px-2" />
          )}
          <div className="-me-2 flex shrink-0 items-center gap-1">
            <AppLauncher />
            <Button variant="ghost" size="sm" className="md:hidden" aria-expanded={open} aria-controls="app-menu" onClick={() => setOpen((o) => !o)}>
              {open ? <X /> : <List />}
              {open ? <Trans>Close</Trans> : <Trans>Menu</Trans>}
            </Button>
          </div>
        </div>

        {/* Following any link inside closes the menu on a phone; the app's router does the navigating. */}
        <div
          id="app-menu"
          className={cn("flex-1 flex-col md:flex", open ? "flex" : "hidden")}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("a")) setOpen(false);
          }}
        >
          {scope && <div className="mt-4 border-b border-line pb-4 md:mt-8">{scope}</div>}
          <nav className={cn("flex flex-col gap-px", scope ? "mt-4" : "mt-4 md:mt-8")} aria-label={t`Primary`}>
            {nav}
          </nav>

          <div className="mt-auto flex flex-col gap-3 pt-6">
            {footer}
            <div className="border-t border-line px-2 pt-4">
              <p className="truncate text-sm text-ink">{user.name ?? user.email ?? <Trans>Signed in</Trans>}</p>
              {user.email && user.name !== user.email && <p className="truncate text-xs text-ink-3">{user.email}</p>}
              <div className="mt-2 -ms-2 flex flex-wrap items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => void signOut()}>
                  <SignOut className="rtl:-scale-x-100" />
                  <Trans>Sign out</Trans>
                </Button>
                <LanguageSwitch onChoose={chooseLanguage} />
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main className="min-w-0 bg-surface">
        <ViewAsBanner switchHref={switchRoleHref} />
        <div className="w-full max-w-[1680px] px-4 py-5 md:px-8 md:py-6">{children}</div>
      </main>
    </div>
  );
}
